// Imports for external libraries go here.
import React, { FC } from 'react';
import dynamic from 'next/dynamic';
import clsx from 'clsx';

import {
  Accordion,
  CardLayered,
  CardLayeredVariations,
  Text,
  Icon,
  Types,
  Button,
  RichText,
} from '@marriott/mi-ui-library';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { usePageContext } from '../../modules';
import {
  AccordionCardWrapperProps,
  AccordionDetailsType,
  AccordionLayeredCardPropsType,
  AccordionCtaDetailsType,
  AccordionDescDetailsType,
} from './AccordionCardWrapper.types';
import {
  StyledAccountAccordionCardWrapper,
  StyledAccountAccordionContentWrapper,
  StyledAccountContentWrapper,
  StyledAccountContentCarouselWrapper,
  StyledAccountAccordionHeader,
  StyledAccountAccordionContent,
} from './AccordionCardWrapper.styles';

// import dynamic content
const CarouselWrapperComponent = dynamic(() =>
  import('../../molecules/CarouselControls').then(mod => mod.CarouselWrapperComponent)
);

// Use named rather than default exports.
export const AccordionCardWrapper: FC<AccordionCardWrapperProps> = props => {
  const { styleClass, model } = props;
  const { accordionDetails, cardCarouselDetails, hideCardDetails } = model;
  const { datalayer } = usePageContext();

  const getGeoTargetedComponentIdsForUserCountry = () => {
    /**
     * hide the complete lane id user is outside of designated country
     */
    const arr = hideCardDetails?.flatMap(key => {
      if (!key.countryName.includes(datalayer?.currentLocCountry)) {
        return [key.componentId];
      }
      return [];
    });
    return arr;
  };

  const AccordionInnerContent = (data: {
    description?: string;
    accordionCtaDetails?: Array<AccordionCtaDetailsType>;
    title?: string;
    iconPath?: string;
    customClass?: string;
    accordionInnerContentExists?: boolean;
  }) => (
    <StyledAccountAccordionContent className={data.customClass}>
      <RenderHeaderText iconPath={data.iconPath} title={data.title} size={Types.size.small} />
      <RichText
        customClass={clsx('t-font-s accordion__desc', data?.accordionInnerContentExists ? 'pb-3' : 'pb-4')}
        componentId=""
        text={data.description || ''}
      />
      <div className="d-flex align-items-center">
        {data?.accordionCtaDetails?.map((key: AccordionCtaDetailsType, i: number) => (
          <Button
            isLink={true}
            key={`button-index-${i}`}
            buttonCopy=""
            href={key?.ctaPath}
            target={key?.openInANewTab ? '_blank' : '_self'}
            className={clsx(
              'd-flex align-tems-center pr-3 t-font-xs',
              !data?.accordionInnerContentExists ? 'pb-4' : ''
            )}
            custom_click_track_value={key?.clickTrackValue}
          >
            {key.ctaIcon === 'icon-external-arrow' ? (
              <>
                <span className="accordion__button-text">{key?.ctaLabel || key?.ctaText}</span>
                <Icon iconClass={clsx('accordion__button-icon pr-2', key?.ctaIcon)} />
              </>
            ) : (
              <>
                <Icon iconClass={clsx('accordion__button-icon pr-2', key?.ctaIcon)} />
                <span className="accordion__button-text">{key?.ctaLabel || key?.ctaText}</span>
              </>
            )}
          </Button>
        ))}
      </div>
    </StyledAccountAccordionContent>
  );

  const RenderHeaderText = (data: { iconPath: string | undefined; title: string | undefined; size: Types.size }) => {
    return data?.title ? (
      <StyledAccountAccordionHeader className="d-flex align-items-center pb-1">
        {data.iconPath && <Icon iconClass={clsx('accordion__title-icon pr-2', data.iconPath)} />}
        <Text
          customClass="accordion__title-text"
          element={Types.tags.span}
          copyText={data.title}
          fontSize={data.size}
        />
      </StyledAccountAccordionHeader>
    ) : (
      <></>
    );
  };

  //accordian  content to render
  const renderAccordion = () =>
    accordionDetails ? (
      accordionDetails?.flatMap((key: AccordionDetailsType, index: number) => (
        <Accordion
          id={`${key.accordionId}-${index}-accordion`}
          key={`${key.accordionId}-${index}-accordion`}
          headerChildren={<RenderHeaderText size={Types.size.medium} iconPath={key.iconPath} title={key.title} />}
          customClass="accordion__wrapper"
          childrenClass="collapsed hide-visibility"
        >
          <AccordionInnerContent
            description={key?.description}
            accordionCtaDetails={
              key?.ctaPath
                ? [
                    {
                      ctaLabel: key.ctaLabel,
                      ctaPath: key.ctaPath,
                      ctaIcon: key.ctaIcon,
                      openInANewTab: key.openInANewTab,
                      clickTrackValue: key?.clickTrackValue,
                    },
                  ]
                : []
            }
          />
          {key?.accordionDescDetails?.map((details: AccordionDescDetailsType, i: number) => (
            <AccordionInnerContent
              {...details}
              customClass={clsx('pb-4 ml-5 accordion__desc--wrapper__second', i !== 0 ? 'border-top pt-4' : '')}
              accordionInnerContentExists={true}
            />
          ))}
        </Accordion>
      ))
    ) : (
      <></>
    );
  const RenderCardContent = () => {
    /**
     * render all card at this place
     */
    const hideCard = getGeoTargetedComponentIdsForUserCountry();

    return cardCarouselDetails ? (
      <CarouselWrapperComponent
        totalSlideLen={cardCarouselDetails?.filter(key => !hideCard?.includes(key.componentId))?.length || 0}
        desktopColumns="2"
        mobileColumns="1"
        tabletColumns="2"
        carousel={true}
        wrapperId="accordian-card"
      >
        {cardCarouselDetails?.flatMap((key: AccordionLayeredCardPropsType) =>
          !hideCard?.includes(key.componentId) ? (
            <div className="glide__slide col">
              <CardLayered
                dynamicMedia={key.featuredDynamicImage}
                dynamicMediaTall={key.featuredDynamicImage}
                cardLayeredVariations={CardLayeredVariations.Tall}
                trackingProperties={{
                  clickTrack: key?.clickTrack,
                  impressionTrack: key?.impressionTrack,
                  trackingContentPosition: key?.trackingContentPosition,
                  trackingDescription: key?.trackingDescription,
                }}
                {...key}
                styleclass="inverse"
              />
            </div>
          ) : (
            <></>
          )
        )}
      </CarouselWrapperComponent>
    ) : (
      <></>
    );
  };
  return (
    <StyledAccountAccordionCardWrapper
      data-testid="account-AccordionCardWrapper"
      data-component-name="o-account-AccordionCardWrapper"
      className={clsx('pb-3 pt-4', styleClass)}
    >
      <StyledAccountContentWrapper className="container">
        <div className="row">
          <StyledAccountAccordionContentWrapper className="col-xl-5 col-12 pb-4 pb-md-0">
            {renderAccordion()}
          </StyledAccountAccordionContentWrapper>
          <StyledAccountContentCarouselWrapper className="col-xl-6 offset-xl-1 col-12 pt-4 pt-md-0">
            <RenderCardContent />
          </StyledAccountContentCarouselWrapper>
        </div>
      </StyledAccountContentWrapper>
    </StyledAccountAccordionCardWrapper>
  );
};
